<template>
  <page-template :is-loading="isLoading" :data-exists="selectedDynamicRuleData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('dynamicRule.configPage.newPageHeadline')
          : $t('dynamicRule.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Dynamic Rules List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedDynamicRule && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name] as [string | number, string])"
          :disabled="isLoading"
          v-model:value="selectedDynamicRule"
          @update:value="switchDynamicRule($event as string)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('dynamicRule.configPage.switchTooltip')"
          classes="switch-dynamic-rule switch-page-entry"
          data-qa="switch-dynamic-rule"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedDynamicRule && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateDynamicRule()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadDynamicRule()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteDynamicRuleModal()"
          class="delete-button"
          :disabled="isLoading"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isDynamicRuleValid"
        :tooltip="saveTooltip"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('dynamicRule.configPage.name')"
              :placeholder="$t('dynamicRule.configPage.name')"
              v-model:inputValue="selectedDynamicRuleData.name"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field rbz-field-toggle">
            <rbz-form-toggle
              v-model:buttonValue="selectedDynamicRuleData.active"
              :label="$t('dynamicRule.configPage.active')"
              data-qa="active-toggle"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('dynamicRule.configPage.description')"
              v-model:inputValue="selectedDynamicRuleData.description"
              rows="2"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field" :class="{ 'is-active': targetActive }">
            <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
              {{ $t('dynamicRule.configPage.target') }}
            </label>
            <div class="rbz-columns">
              <div class="rbz-column">
                <rbz-dropdown
                  :items="
                    targetOptions.map((item) => [item.key, item.title] as [string | number, string])
                  "
                  data-qa="target-dropdown"
                  v-model:value="targetType"
                  @is-open="targetActive = $event"
                  @update:value="targetDropDownChanged"
                  classes="target-dropdown"
                  :disabled="readOnly"
                />
              </div>
              <div class="rbz-column">
                <rbz-form-input
                  v-if="isTargetArgsCookiesHeaders(targetType)"
                  classes="target-key-input"
                  name="target-key-input"
                  data-qa="target-key-input"
                  :placeholder="$t('dynamicRule.configPage.targetKey')"
                  @focus="targetActive = true"
                  @blur="targetActive = false"
                  v-model:inputValue="targetValue"
                  @change:inputValue="targetChanged"
                  :disabled="readOnly"
                  :is-invalid="!isTargetValueValid"
                  :error-message-text="targetValueError"
                />
                <div
                  v-if="showOffloadIpBlocking && targetType === 'ip'"
                  class="offload-ip-tooltip"
                  v-b-tooltip="
                    !readOnly ? $t('dynamicRule.configPage.offloadIpBlockingTooltip') : ''
                  "
                >
                  <rbz-form-toggle
                    class="target-key-toggle"
                    v-model:buttonValue="selectedDynamicRuleData.offload_ip_filtering"
                    :label="$t('dynamicRule.configPage.offloadIpBlocking')"
                    :disabled="shouldHideOffloadIpBlocking || readOnly"
                  />
                </div>
              </div>
            </div>
            <div
              class="offload-ip-help"
              v-if="showOffloadIpBlocking && selectedDynamicRuleData.offload_ip_filtering"
            >
              <div class="offload-ip-help-icon">
                <icon-info-small color="dusty-gray" />
              </div>
              <div class="offload-ip-help-text">
                {{ $t('dynamicRule.configPage.offloadIpBlockingHelpText') }}
              </div>
            </div>
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="threshold"
              data-qa="threshold-input"
              classes="threshold"
              type="number"
              number-type="whole"
              :label="$t('dynamicRule.configPage.threshold')"
              :placeholder="$t('dynamicRule.configPage.threshold')"
              v-model:inputValue="selectedDynamicRuleData.threshold"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="timeframe"
              data-qa="timeframe-input"
              classes="timeframe"
              type="number"
              number-type="whole"
              :label="$t('dynamicRule.configPage.timeFrame')"
              :placeholder="$t('dynamicRule.configPage.timeFrame')"
              suffix="minutes"
              :is-invalid="!isTimeframeValid"
              :error-message-text="timeFrameError"
              v-model:inputValue="selectedDynamicRuleData.timeframe"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              v-if="actions.length"
              :items="actions.map((item) => [item.id, item.name] as [string | number, string])"
              v-model:value="selectedDynamicRuleData.action"
              :label="$t('dynamicRule.configPage.action')"
              data-qa="action-dropdown"
              @update:value="actionChanged($event as string)"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="quarantine-time"
              class="quarantine-time"
              :label="$t('dynamicRule.configPage.quarantineTime')"
              :placeholder="$t('dynamicRule.configPage.quarantineTime')"
              suffix="hours"
              data-qa="quarantine-time-input"
              type="number"
              number-type="whole"
              :is-invalid="!isQuarantineTimeValid"
              :error-message-text="quarantineTimeError"
              :disabled="readOnly"
              v-model:inputValue="quarantineTime"
            />
          </div>
          <div class="rbz-field">
            <tags-selection
              :value="selectedDynamicRuleData.tags"
              @update:value="selectedDynamicRuleData.tags = $event as string[]"
              :label="$t('dynamicRule.configPage.tags')"
              :disabled="readOnly"
            />
            <div class="automatic-tags" :disabled="readOnly">
              <template v-for="tag in automaticTags" :key="tag">
                <rbz-custom-tag :value="tag" :disabled="readOnly" />
              </template>
            </div>
          </div>
        </div>
        <div class="rbz-column is-8">
          <tags-selection-table
            :columns="tagsSelectionTableColumns"
            :show-slider-toggle="true"
            @update:tags="updateTags($event.fieldName as IncludeExcludeType, $event.tags)"
            @update:tags-relation="
              updateRelation($event.fieldName as IncludeExcludeType, $event.relation)
            "
            :disabled="readOnly"
          />
        </div>
      </div>
      <rbz-modal-dialog
        :show="showDeleteDynamicRuleModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteDynamicRule"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteDynamicRuleModal = false"
        @closeModal="showDeleteDynamicRuleModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedDynamicRuleData.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import {
  BasicDocument,
  Action,
  DocumentName,
  DynamicRule,
  TagsSelectionTableColumn,
  IncludeExcludeType,
  Relation,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import TagsSelection from '@/components/TagsSelection.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import TagsSelectionTable from '@/components/TagsSelectionTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { usePlanetStore } from '@/store/PlanetStore';
import { useTagsStore } from '@/store/TagsStore';
import RbzCustomTag from '@/components/RbzCustomTag.vue';
import {
  TARGET_OPTIONS,
  MAX_QUARANTINE_TIME_HOURS,
  MAX_QUARANTINE_TIME_HOURS_OFFLOAD_IP,
} from '@/helpers/Consts';
import {
  shouldUseMockData,
  getDynamicRulesMockData,
  getActionsMockData,
} from '@/helpers/MockDataUtils';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';
import { useUserStore } from '@/store/UserStore';
import { ENV } from '@/constants';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'DynamicRulesEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    IconInfoSmall,
    RbzCustomTag,
    RbzFormButton,
    RbzFormInput,
    RbzFormTextarea,
    RbzFormToggle,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
    TagsSelection,
    TagsSelectionTable,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedDynamicRule: null as string,
      selectedDynamicRuleData: null as DynamicRule,
      originalDynamicRuleData: null as DynamicRule,
      pageLoadingCounter: 0,
      isDeleteLoading: false,

      actions: null as Action[],
      targetOptions: _.map(TARGET_OPTIONS, (targetOption) => {
        return {
          key: targetOption.key,
          title: this.$t(targetOption.translationKey),
        };
      }),
      targetType: '',
      targetValue: '',
      targetActive: false,
      showDeleteDynamicRuleModal: false,
      publicCloudProviders: ['gcp', 'aws'],
      provider: ENV.VITE_LOAD_BALANCER_PROVIDER,
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, usePlanetStore, useTagsStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName(): DocumentName {
      return {
        plural: this.$t('dynamicRule.plural'),
        singular: this.$t('dynamicRule.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter > 0 || this.isDeleteLoading;
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedDynamicRuleData, this.originalDynamicRuleData);
    },

    automaticTags(): string[] {
      const nameTag = `dynamic-rule:${this.tagsStore.textToTag(this.selectedDynamicRuleData.name)}`;
      return [nameTag];
    },

    tagsSelectionTableColumns(): TagsSelectionTableColumn[] {
      return [
        {
          title: this.$t('common.include'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'include',
              tags: this.selectedDynamicRuleData['include'].tags,
              relation: this.selectedDynamicRuleData['include'].relation,
              tagVariant: (tag: string) => {
                if (this.duplicateTags.includes(tag)) {
                  return 'error';
                }
                return 'fill';
              },
            },
          ],
          classes: 'is-6',
        },
        {
          title: this.$t('common.exclude'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'exclude',
              tags: this.selectedDynamicRuleData['exclude'].tags,
              relation: this.selectedDynamicRuleData['exclude'].relation,
              tagVariant: (tag: string) => {
                if (this.duplicateTags.includes(tag)) {
                  return 'error';
                }
                return 'fill';
              },
            },
          ],
          classes: 'is-6',
        },
      ];
    },

    duplicateTags(): string[] {
      const allTags = _.concat(
        this.selectedDynamicRuleData['include'].tags,
        this.selectedDynamicRuleData['exclude'].tags
      );
      return _.filter(allTags, (val, i, iteratee) => _.includes(iteratee, val, i + 1));
    },

    isDynamicRuleValid(): boolean {
      const noDuplicates = this.duplicateTags.length === 0;
      const trimmedName = this.selectedDynamicRuleData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedDynamicRuleData.name &&
        (trimmedName === this.originalDynamicRuleData.name.toLowerCase().trim() || !nameExists);
      const quarantineTimeValid = this.isQuarantineTimeValid;
      const targetValueValid = this.isTargetValueValid;
      return (
        noDuplicates &&
        nameValid &&
        quarantineTimeValid &&
        targetValueValid &&
        this.isTimeframeValid
      );
    },

    saveTooltip(): string {
      if (this.isLoading) {
        return '';
      }
      if (!this.isQuarantineTimeValid) {
        return this.quarantineTimeError;
      }
      if (!this.isTargetValueValid) {
        return this.targetValueError;
      }
      if (!this.isTimeframeValid) {
        return this.$t('dynamicRule.configPage.timeFrameInvalid');
      }
      return '';
    },

    shouldHideOffloadIpBlocking(): boolean {
      const actionType = _.find(this.actions, (action) => {
        return this.selectedDynamicRuleData.action === action.id;
      });
      return actionType?.type !== 'block';
    },

    showOffloadIpBlocking(): boolean {
      return this.provider && !this.publicCloudProviders.includes(this.provider);
    },

    quarantineTime: {
      // Convert saved seconds to be displayed in hours
      get() {
        return this.selectedDynamicRuleData.ttl / 3600;
      },
      // Convert received hours to be saved in seconds
      set(newValue: number) {
        this.selectedDynamicRuleData.ttl = newValue * 3600;
      },
    },

    quarantineTimeError(): string {
      if (this.quarantineTime % 1 !== 0 || this.quarantineTime < 1) {
        return this.$t('dynamicRule.configPage.quarantineTimeNotPositiveInteger');
      }
      if (
        this.selectedDynamicRuleData.offload_ip_filtering &&
        this.quarantineTime > MAX_QUARANTINE_TIME_HOURS_OFFLOAD_IP
      ) {
        return this.$t('dynamicRule.configPage.quarantineTimeExceedsMaxOffloadIp');
      }
      if (this.quarantineTime > MAX_QUARANTINE_TIME_HOURS) {
        return this.$t('dynamicRule.configPage.quarantineTimeExceedsMax');
      }
      return '';
    },

    isQuarantineTimeValid(): boolean {
      return !this.quarantineTimeError;
    },

    targetValueError(): string {
      if (this.isTargetArgsCookiesHeaders(this.targetType)) {
        const trimmedValue = this.targetValue.trim();
        if (!trimmedValue) {
          return this.$t('dynamicRule.configPage.targetValueEmpty');
        }
        if (/\s/.test(trimmedValue)) {
          return this.$t('dynamicRule.configPage.targetKeyNoSpaces');
        }
      }
      return '';
    },

    isTargetValueValid(): boolean {
      return !this.targetValueError;
    },

    isTimeframeValid(): boolean {
      return this.selectedDynamicRuleData.timeframe > 0;
    },
    timeFrameError(): string {
      if (!this.isTimeframeValid) {
        return this.$t('dynamicRule.configPage.timeFrameInvalid');
      }
      return '';
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('dynamic-rules'));
    },

    switchDynamicRule(dynamicRuleId: string) {
      this.navigateTo(this.getEditPath('dynamic-rules', dynamicRuleId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getDynamicRulesMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/dynamic-rules`,
          config: { headers: { 'x-fields': 'id, name' } },
        });
      }
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newDynamicRule(): DynamicRule {
      const factory = DatasetsUtils.newEntityFactory['dynamic-rules'];
      return factory && factory();
    },

    async loadDynamicRule(dynamicRuleId: string) {
      this.setPageLoadingStatus(true);
      this.selectedDynamicRule = dynamicRuleId;
      let response;
      if (shouldUseMockData()) {
        const mockItems = getDynamicRulesMockData()?.items || [];
        const found = mockItems.find((item) => item.id === dynamicRuleId);
        response = { data: found || mockItems[0] || null };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/dynamic-rules/${dynamicRuleId}`,
        });
      }
      this.selectedDynamicRuleData = response?.data || {};
      this.originalDynamicRuleData = _.cloneDeep(this.selectedDynamicRuleData);
      this.updateTargetDisplay();
      this.setPageLoadingStatus(false);
    },

    updateTargetDisplay() {
      if (this.selectedDynamicRuleData?.target.includes('_')) {
        const targets = this.selectedDynamicRuleData.target.split('_');
        this.targetType = targets.shift();
        this.targetValue = targets.join('_');
      } else {
        this.targetType = this.selectedDynamicRuleData.target;
        this.targetValue = '';
      }
    },

    async duplicateDynamicRule() {
      this.setPageLoadingStatus(true);
      const dynamicRuleToAdd = _.cloneDeep(this.selectedDynamicRuleData);
      dynamicRuleToAdd.id = DatasetsUtils.generateUUID2();
      dynamicRuleToAdd.name = Utils.generateUniqueEntityName(
        dynamicRuleToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/dynamic-rules/${dynamicRuleToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: dynamicRuleToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchDynamicRule(dynamicRuleToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadDynamicRule() {
      Utils.downloadFile('dynamic-rule', 'json', this.selectedDynamicRuleData);
    },

    openDeleteDynamicRuleModal() {
      this.showDeleteDynamicRuleModal = true;
    },

    async deleteDynamicRule() {
      this.isDeleteLoading = true;
      const dynamicRuleId = this.selectedDynamicRule;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === dynamicRuleId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedDynamicRule = null;
      // Delete Dynamic Rule
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/dynamic-rules/${dynamicRuleId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      // Save Dynamic Rule
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedDynamicRuleData.name = this.selectedDynamicRuleData.name.trim();
      this.targetValue = this.targetValue.trim();
      this.targetChanged();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/dynamic-rules/${this.selectedDynamicRuleData.id}`,
        data: this.selectedDynamicRuleData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedDynamicRuleData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalDynamicRuleData = _.cloneDeep(this.selectedDynamicRuleData);
        }
      }
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      const displayedDefaultIds = [
        'action-dynamic-rule-block',
        'action-waap-feed-block',
        'action-https-redirect',
        'action-challenge',
        'action-monitor',
        'action-skip',
      ];
      const displayedTypes: Action['type'][] = [
        'skip',
        'block',
        'challenge',
        'ichallenge',
        'monitor',
      ];
      let response;
      if (shouldUseMockData()) {
        response = { data: getActionsMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/actions`,
          config: { headers: { 'x-fields': 'id, name, type' } },
        });
      }
      this.actions = _.filter(response?.data?.items, (item: Action) => {
        const hiddenDefaultId =
          item.id.startsWith('action-') && !displayedDefaultIds.includes(item.id);
        return displayedTypes.includes(item.type) && !hiddenDefaultId;
      });
      this.setPageLoadingStatus(false);
    },

    targetDropDownChanged() {
      this.targetValue = '';
      this.targetChanged();
      this.selectedDynamicRuleData.offload_ip_filtering = false;
    },

    targetChanged() {
      if (this.isTargetArgsCookiesHeaders(this.targetType)) {
        this.selectedDynamicRuleData.target = `${this.targetType}_${this.targetValue.trim()}`;
      } else {
        this.selectedDynamicRuleData.target = this.targetType;
      }
    },

    isTargetArgsCookiesHeaders(target: string): boolean {
      return new RegExp('(arguments|cookies|headers)').test(target);
    },

    updateRelation(fieldName: IncludeExcludeType, newRelation: Relation): void {
      this.selectedDynamicRuleData[fieldName].relation = newRelation;
    },

    updateTags(fieldName: IncludeExcludeType, tags: string[]): void {
      this.selectedDynamicRuleData[fieldName].tags = tags;
    },

    actionChanged(newActionId: string): void {
      const newAction = _.find(this.actions, (action) => {
        return action.id === newActionId;
      });
      if (newAction.type !== 'block') {
        this.selectedDynamicRuleData.offload_ip_filtering = false;
      }
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedDynamicRuleData, this.entityNotSaved] as [DynamicRule, boolean],
        ([formData, entityNotSaved]) => {
          Utils.saveSessionData(formData, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  created() {
    const tagsStore = useTagsStore();
    this.$watch(
      () => [tagsStore.editorDocId, tagsStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'dynamic-rules') {
          return;
        }
        await this.branchesStore.getBranches();
        await this.loadActions();
        await this.loadDynamicRule(docId);
      },
      { immediate: true }
    );
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Dynamic Rule', 'Dynamic Rules Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          const dropdownDataPromise = this.loadDropdownData();
          const actionsPromise = this.loadActions();
          await Promise.all([dropdownDataPromise, actionsPromise]);
          if (this.isNew) {
            this.selectedDynamicRuleData = this.newDynamicRule();
            this.selectedDynamicRuleData.name = Utils.generateUniqueEntityName(
              this.selectedDynamicRuleData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalDynamicRuleData = _.cloneDeep(this.selectedDynamicRuleData);
            this.updateTargetDisplay();
          } else {
            await this.loadDropdownData();
            const contentFilterRuleId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === contentFilterRuleId;
            });
            if (dataExists) {
              await this.loadDynamicRule(contentFilterRuleId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedDynamicRuleData = JSON.parse(savedSessionData);
            this.updateTargetDisplay();
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
<style lang="scss" scoped>
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.target-key-toggle {
  height: 100%;
}

.offload-ip-help {
  background-color: map.get(colors.$colors, 'polar-mist');
  padding: 8px;
  display: flex;
  flex-direction: row;
  border-radius: 2px;
}

.offload-ip-help-text {
  flex-grow: 1;
  font-size: 10px;
  line-height: 16px;
  padding: 2px auto auto 2px;
}

.offload-ip-help-icon svg {
  height: 20px;
  width: 20px;
  padding: 2px auto auto 2px;
  gap: 10px;
  margin-right: 3px;
}

.offload-ip-tooltip {
  padding: 8px 0;
}
</style>
